import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/**
 * Chemical breakdown within a sponsor's points summary
 */
export interface ChemicalPointsBreakdown {
  chemicalId: string;
  chemicalName: string;
  category: string | null;
  points: number;
}

/**
 * Per-sponsor points breakdown for the dashboard
 */
export interface SponsorPointsBreakdown {
  sponsorId: string;
  sponsorName: string;
  sponsorLogoUrl: string | null;
  brandColor: string | null;
  totalPoints: number;
  chemicals: ChemicalPointsBreakdown[];
}

/**
 * Missed points breakdown item for the dashboard
 * Represents a sponsor chemical the organization could have used
 * but did not, along with the points they missed out on.
 *
 * Note: This data will be populated once the chemical equivalence
 * mapping approach is decided. Until then, the API returns an empty array.
 */
export interface MissedPointsBreakdown {
  chemicalName: string;
  category: string | null;
  recordCount: number;
  points: number;
}

/**
 * Complete dashboard data returned by the Acres Advantage API
 * Matches the hub API response structure from AcresAdvantageService
 */
export interface AcresAdvantageDashboard {
  season: {
    id: string;
    label: string;
    startDate: string;
    endDate: string;
  } | null;
  totalPointsEarned: number;
  eligibleRecordsUsed: number;
  totalSprayRecords: number;
  totalPointsMissed: number;
  sponsorBreakdowns: SponsorPointsBreakdown[];
  missedBreakdowns: MissedPointsBreakdown[];
}

/**
 * API Response wrapper
 */
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Acres Advantage Service
 *
 * Fetches Acres Advantage dashboard data from the Hub API.
 * Returns points earned, sponsor breakdowns, and summary statistics.
 *
 * Reference: /agent-os/specs/2026-02-11-acres-advantage/spec.md
 */
@Injectable({
  providedIn: 'root'
})
export class AcresAdvantageService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  /**
   * Get dashboard data for the current organization
   * API: GET /organizations/:organizationId/acres-advantage
   */
  getDashboardData(organizationId: string): Observable<AcresAdvantageDashboard> {
    return this.http
      .get<ApiResponse<AcresAdvantageDashboard>>(
        `${this.apiUrl}/organizations/${organizationId}/acres-advantage`
      )
      .pipe(
        map(response => {
          if (response.success && response.data) {
            return response.data;
          }
          throw new Error(response.error || 'Failed to load Acres Advantage dashboard data');
        })
      );
  }
}
